import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { IMG, PHONE_DISPLAY, PHONE_TEL } from "../../lib/site";
import { SERVICES_DATA } from "../../lib/services";

const MAIN_LINKS = [
  { label: "Home", to: "/" },
  { label: "Commercial", to: "/commercial" },
  { label: "Gallery", to: "/gallery" },
  { label: "Reviews", to: "/reviews" },
  { label: "About", to: "/about" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setSvcOpen(false);
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  return (
    <header className="sticky top-0 z-50 bg-black/85 backdrop-blur-xl border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3 group shrink-0" data-testid="nav-logo-link">
          <img src={IMG.logo} alt="PRYME EPOXY logo" className="h-11 w-auto object-contain" />
          <div className="flex flex-col leading-none">
            <span className="font-heading text-lg sm:text-xl font-bold tracking-wider text-white group-hover:text-[#C69453] transition-colors">PRYME EPOXY</span>
            <span className="text-[10px] text-zinc-400 tracking-[0.25em] uppercase font-semibold mt-1">Northern Arizona</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center space-x-5 xl:space-x-6">
          <Link to="/" className={`text-[15px] font-medium ${isActive("/") ? "text-[#C69453]" : "text-zinc-300 hover:text-[#C69453]"} transition-colors`} data-testid="nav-link-home">Home</Link>

          <div className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)} onFocus={() => setSvcOpen(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSvcOpen(false); }}>
            <Link to="/services" className={`flex items-center gap-1 text-[15px] font-medium ${location.pathname.startsWith("/services") ? "text-[#C69453]" : "text-zinc-300 hover:text-[#C69453]"} transition-colors`} data-testid="nav-link-services">
              Services <ChevronDown className="w-4 h-4" />
            </Link>
            {svcOpen && (
              <div className="absolute left-0 top-full pt-3 w-72" data-testid="nav-services-dropdown">
                <div className="bg-black border border-zinc-800 shadow-2xl py-2">
                  {SERVICES_DATA.map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="block px-4 py-2.5 text-zinc-300 hover:text-[#C69453] hover:bg-zinc-900 text-sm" data-testid={`nav-service-${s.slug}`}>
                      {s.name}
                    </Link>
                  ))}
                  <Link to="/services" className="block px-4 py-2.5 text-[#C69453] font-semibold text-sm border-t border-zinc-800 mt-1">All Services →</Link>
                </div>
              </div>
            )}
          </div>

          {MAIN_LINKS.slice(1).map((l) => (
            <Link key={l.to} to={l.to} className={`text-[15px] font-medium ${isActive(l.to) ? "text-[#C69453]" : "text-zinc-300 hover:text-[#C69453]"} transition-colors whitespace-nowrap`} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center space-x-3 shrink-0">
          <a href={PHONE_TEL} data-testid="nav-phone-button" className="hidden sm:flex items-center space-x-2 bg-zinc-900 border border-[#C69453] hover:bg-[#C69453] hover:text-black text-[#C69453] px-3 py-2.5 transition-colors font-bold text-sm">
            <Phone className="w-4 h-4" /><span>{PHONE_DISPLAY}</span>
          </a>
          <Link to="/contact" data-testid="nav-estimate-button" data-track="cta_estimate" data-track-label="navbar" className="hidden md:block bg-[#C69453] hover:bg-[#AE7F44] text-black font-bold px-4 py-2.5 transition-colors text-sm whitespace-nowrap">Free Estimate</Link>
          <button onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Toggle menu" className="lg:hidden p-2 text-white border border-zinc-800 bg-zinc-950">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-800 bg-black max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <nav className="max-w-7xl mx-auto px-5 py-4 flex flex-col">
            <Link to="/" className="py-3 text-lg font-medium border-b border-zinc-900 text-zinc-200" data-testid="nav-mobile-link-home">Home</Link>
            <Link to="/services" className="py-3 text-lg font-medium border-b border-zinc-900 text-[#C69453]" data-testid="nav-mobile-link-services">All Services</Link>
            <div className="border-b border-zinc-900 py-1">
              {SERVICES_DATA.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="block py-2 pl-4 text-base text-zinc-400">{s.name}</Link>
              ))}
            </div>
            {[...MAIN_LINKS.slice(1), { label: "Process", to: "/process" }, { label: "FAQ", to: "/faq" }].map((l) => (
              <Link key={l.to} to={l.to} className="py-3 text-lg font-medium border-b border-zinc-900 text-zinc-200" data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </Link>
            ))}
            <a href={PHONE_TEL} className="mt-4 flex items-center justify-center space-x-2 bg-[#C69453] text-black font-bold py-4 text-lg">
              <Phone className="w-5 h-5" /><span>Call Pryme: {PHONE_DISPLAY}</span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
